import { supabase } from './supabase'
import { addDays, formatISO, startOfDay } from 'date-fns'

export async function getCounts(opts?: { organizerId?: number }) {
  let events = 0
  let bookings = 0
  const { count: organizers } = await supabase.from('organizers').select('*', { count: 'exact', head: true })
  const { count: users } = await supabase.from('profiles').select('*', { count: 'exact', head: true })
  const { count: editors } = await supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'editor')
  if (opts?.organizerId) {
    const { count: ev } = await supabase.from('movies').select('*', { count: 'exact', head: true }).eq('organizer_id', opts.organizerId)
    events = ev || 0
    const { data: orgMovies } = await supabase.from('movies').select('id').eq('organizer_id', opts.organizerId)
    const mids = (orgMovies || []).map((m: any) => m.id)
    if (mids.length) {
      const { data: screens } = await supabase.from('screenings').select('id').in('movie_id', mids)
      const sids = (screens || []).map((s: any) => s.id)
      if (sids.length) {
        const { count: b } = await supabase.from('bookings').select('*', { count: 'exact', head: true }).in('screening_id', sids)
        bookings = b || 0
      } else bookings = 0
    } else bookings = 0
  } else {
    const { count: ev } = await supabase.from('movies').select('*', { count: 'exact', head: true })
    events = ev || 0
    const { count: b } = await supabase.from('bookings').select('*', { count: 'exact', head: true })
    bookings = b || 0
  }
  return { events, organizers: organizers || 0, bookings, users: users || 0, editors: editors || 0 }
}

export async function getTimeSeriesBookings(days = 30, opts?: { organizerId?: number; movieId?: number }) {
  const since = formatISO(addDays(new Date(), -days))
  let q = supabase
    .from('bookings')
    .select('id, created_at, status, total_amount, screening_id')
    .gte('created_at', since)
    .order('created_at', { ascending: true })
  if (opts?.organizerId || opts?.movieId) {
    const { data: screens } = await supabase
      .from('screenings')
      .select('id')
      .in('movie_id', (
        opts?.movieId
          ? [opts.movieId]
          : ((await supabase.from('movies').select('id').eq('organizer_id', opts?.organizerId as number)).data || []).map((m: any) => m.id)
      ))
    const sids = (screens || []).map((s: any) => s.id)
    if (sids.length) q = q.in('screening_id', sids)
    else return []
  }
  const { data } = await q
  const buckets: Record<string, { count: number; revenue: number }> = {}
  for (const b of (data || []) as any[]) {
    const d = startOfDay(new Date(b.created_at))
    const key = d.toISOString()
    if (!buckets[key]) buckets[key] = { count: 0, revenue: 0 }
    buckets[key].count += 1
    if (typeof b.total_amount === 'number') buckets[key].revenue += b.total_amount
  }
  const series = Object.keys(buckets).sort().map(k => ({ date: k, count: buckets[k].count, revenue: buckets[k].revenue }))
  return series
}

export async function getUpcomingOccupancy(limit = 6, opts?: { organizerId?: number; movieId?: number }) {
  const nowIso = new Date().toISOString()
  let q = supabase
    .from('screenings')
    .select('id, movie_id, start_time, location')
    .gte('start_time', nowIso)
    .order('start_time', { ascending: true })
    .limit(limit)
  if (opts?.movieId) q = q.eq('movie_id', opts.movieId)
  else if (opts?.organizerId) q = q.in('movie_id', ((await supabase.from('movies').select('id').eq('organizer_id', opts.organizerId)).data || []).map((m: any) => m.id))
  const { data: upcoming } = await q
  const screenings = (upcoming || []) as { id: number; movie_id: number; start_time: string; location: string }[]
  const ids = screenings.map(s => s.id)
  let capacityById: Record<number, number> = {}
  let bookedById: Record<number, number> = {}
  if (ids.length) {
    const [{ data: layouts }, { data: splits }, { data: bs }, { data: bookings }] = await Promise.all([
      supabase.from('seat_layouts').select('*').in('screening_id', ids),
      supabase.from('seatssplitting').select('*').in('screening_id', ids),
      supabase.from('booking_seats').select('screening_id, seat_ids, seat_id').in('screening_id', ids),
      supabase.from('bookings').select('screening_id, status, seats').in('screening_id', ids),
    ])
    for (const l of ((layouts || []) as any[])) {
      let cap = 0
      const seats: boolean[][] = l.seats as any
      for (let r = 0; r < seats.length; r++) for (let c = 0; c < seats[r].length; c++) if (seats[r][c]) cap++
      capacityById[l.screening_id as number] = cap
    }
    for (const s of ((splits || []) as any[])) {
      const tot = (s.silver_total || 0) + (s.gold_total || 0) + (s.diamond_total || 0)
      if (!capacityById[s.screening_id as number]) capacityById[s.screening_id as number] = tot
      const booked = (s.silver_booked || 0) + (s.gold_booked || 0) + (s.diamond_booked || 0)
      bookedById[s.screening_id as number] = Math.max(bookedById[s.screening_id as number] || 0, booked)
    }
    for (const row of ((bs || []) as any[])) {
      const sid = Number(row.screening_id)
      const count = Array.isArray(row.seat_ids) ? (row.seat_ids as any[]).length : (row.seat_id ? 1 : 0)
      bookedById[sid] = (bookedById[sid] || 0) + count
    }
    for (const b of ((bookings || []) as any[])) {
      if ((b.status || '') === 'confirmed') {
        const sid = Number(b.screening_id)
        bookedById[sid] = (bookedById[sid] || 0) + Number(b.seats || 0)
      }
    }
  }
  return screenings.map(s => ({
    id: s.id,
    movie_id: s.movie_id,
    start_time: s.start_time,
    location: s.location,
    capacity: capacityById[s.id] || 0,
    booked: Math.min(bookedById[s.id] || 0, capacityById[s.id] || bookedById[s.id] || 0),
  }))
}

export async function getTopEvents(limit = 5, opts?: { organizerId?: number }) {
  const { data: screenings } = await supabase
    .from('screenings')
    .select('id, movie_id')
  const screenById: Record<number, number> = {}
  for (const s of ((screenings || []) as any[])) screenById[s.id as number] = s.movie_id as number
  const { data: bookings } = await supabase
    .from('bookings')
    .select('screening_id, status, seats')
    .eq('status', 'confirmed')
  let orgSet: Set<number> | null = null
  if (opts?.organizerId) {
    const { data: orgMovies } = await supabase.from('movies').select('id').eq('organizer_id', opts.organizerId)
    orgSet = new Set((orgMovies || []).map((m: any) => m.id))
  }
  const tally: Record<number, number> = {}
  for (const b of ((bookings || []) as any[])) {
    const mid = screenById[Number(b.screening_id)]
    if (!mid) continue
    if (orgSet && !orgSet.has(mid)) continue
    tally[mid] = (tally[mid] || 0) + Number(b.seats || 0)
  }
  const { data: movies } = await supabase
    .from('movies')
    .select('id, title')
    .in('id', Object.keys(tally).map(n => Number(n)))
  const titleById: Record<number, string> = {}
  for (const m of ((movies || []) as any[])) titleById[m.id as number] = String(m.title || 'Untitled')
  const rows = Object.keys(tally).map(k => ({ movie_id: Number(k), title: titleById[Number(k)] || 'Untitled', tickets: tally[Number(k)] }))
  rows.sort((a, b) => b.tickets - a.tickets)
  return rows.slice(0, limit)
}

export async function getRoleCounts() {
  const roles = ['organizer', 'validator', 'editor', 'admin']
  const results: Record<string, number> = {}
  await Promise.all(roles.map(async r => {
    const { count } = await supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', r)
    results[r] = count || 0
  }))
  return results
}
